/*
 * 戒导航 · 提及模块（jiedh/mention）—— 方案 B 的「三腿」注册
 * =============================================================================
 * 设计依据：packages/mention/extend.php 头部注释「三、做法（自研 Tiptap Node，完全不侵入 vendor/）」
 *           doc/输入框富文本编辑器设计方案.md §8.5（方案 B 机制）
 *
 * fof/rich-text 的富文本编辑器由三个可扩展的构件组成，三者都通过 Flarum 的公开扩展点
 * `extend()` 挂接（**不修改 vendor/**）：
 *
 *   腿 A · TiptapEditorDriver.buildExtensions()   —— 往编辑器扩展列表里加自研节点 mention
 *   腿 B · MarkdownParserBuilder.buildTokenizer() —— 让 markdown-it 认得出 @"名"#p34 / @用户名 / #标签
 *          MarkdownParserBuilder.buildTokens()    —— 把这个 token 映射成上面的节点
 * 腿 C · MarkdownSerializerBuilder.buildNodes()  —— 把节点**原样**还原回提及源码
 * 补丁 D · TiptapEditorDriver.replaceBeforeCursor() —— 自动完成下拉选中的提及直接插入为节点
 *          （见文件末尾 registerAutocompleteInsertion 的详细说明）
 *
 * 三者必须同批交付：
 *   · 只做 B 不做 C → 提交时序列化器遇到未注册的节点会抛错；
 *   · 只做 B 不做 A → parser 找不到名为 mention 的节点，同样抛错。
 *
 * 【为什么用字符串路径而不是 import】
 *   `extend('ext:命名空间/模块路径', …)` 会被解析为 flarum.reg.onLoad(namespace, id, …)：
 *   模块若尚未加载则先挂起、等它注册时再执行，因此与扩展加载顺序无关，
 *   也无需把 fof/rich-text 的前端打进我们自己的 bundle。
 *   命名空间由 `fof/rich-text` 推导为 `fof-rich-text`（ExportRegistry::namespaceAndIdFromPath）。
 *   三个 id 均取自 fof/rich-text 构建产物里的 flarum.reg.add 语句，见
 *   TiptapEditorDriver.js / MarkdownParserBuilder.js / MarkdownSerializerBuilder.js 的注册清单。
 *
 * 【关于 extend 回调的首行判空】
 *   Flarum 的 extend() 在目标方法不存在时会把 value 传成 undefined 仍照常调用回调，
 *   因此每个回调都必须先判空再使用，避免抛错影响编辑器整体构建。
 */
import { extend, override } from 'flarum/common/extend';
import { MENTION_NODE, MENTION_TOKEN, formatMentionSource, parseMentionSource, tokenizeMention } from './mentionSyntax';
import { createMentionNode } from './MentionNode';

/** 腿 A 宿主：编辑器驱动类。 */
const TIPTAP_EDITOR_DRIVER = 'ext:fof/rich-text/common/tiptap/TiptapEditorDriver';

/** 腿 B 宿主：markdown-it 分词器与 token 表的构建器。 */
const MARKDOWN_PARSER_BUILDER = 'ext:fof/rich-text/common/tiptap/markdown/MarkdownParserBuilder';

/** 腿 C 宿主：Markdown 序列化器的构建器。 */
const MARKDOWN_SERIALIZER_BUILDER = 'ext:fof/rich-text/common/tiptap/markdown/MarkdownSerializerBuilder';

/**
 * 腿 A —— 注册自研 Tiptap 节点 mention。
 *
 * buildExtensions() 返回的是一个全新的 ItemList（每次编辑器构建都会重建），
 * 所以在这里原地 add 不会重复、也不影响内置扩展。
 *
 * 关于排序：ItemList.add(key, content, priority = 0)，toArray() 按 priority **降序**排序，
 * 而 V8 的 Array.prototype.sort 是稳定的 ⇒ 同为 0 优先级时保持 add 顺序。
 * mention 是内联原子节点，与内置节点的解析无先后冲突（其语法由腿 B 的 tokenizer 独占地
 * 从 @ / # 起始处识别），因此无需关心它在列表中的相对位置。
 */
function registerNode() {
  extend(TIPTAP_EDITOR_DRIVER, 'buildExtensions', function (items) {
    if (!items || typeof items.add !== 'function') return;
    const node = createMentionNode();
    if (!node) return; // 取不到 Tiptap primitives 时降级跳过（getTiptapPrimitives 已打日志）

    items.add(MENTION_NODE, node);
  });
}

/**
 * 腿 B —— 让解析链路认得出提及语法。
 *
 * 分词：插在 markdown-it 的 'link' 规则之前。位置刚刚好：
 *   · fof/rich-text 的 altText 规则（vendor/fof/rich-text/js/src/common/tiptap/markdown/it/altText.ts）
 *     把 @ (0x40) 与 # (0x23) 都列为终止符，文本规则会在它们之前停下；
 *   · 停下之后、link 之前，正好轮到本规则从 @ / # 起始处独占地吃掉整段提及源码。
 * 映射：把 token 类型登记成「node 型」，getAttrs 负责把 token.content 还原成节点属性。
 *   getAttrs 的签名是 (token, tokens, index)，本项目只需要第一个参数。
 */
function registerParser() {
  extend(MARKDOWN_PARSER_BUILDER, 'buildTokenizer', function (markdownIt) {
    if (!markdownIt || !markdownIt.inline || !markdownIt.inline.ruler) return;
    markdownIt.inline.ruler.before('link', MENTION_TOKEN, tokenizeMention);
  });
  extend(MARKDOWN_PARSER_BUILDER, 'buildTokens', function (tokens) {
    if (!tokens) return;
    tokens[MENTION_TOKEN] = {
      node: MENTION_NODE,
      // token.content 里存的就是原始切片（tokenizeMention 写入），这里原样还原成 Node 属性。
      // parseMentionSource 理论上不会失败（切片本身就是它命中的），兜底返回最小属性对象，
      // 保留 raw 以保证序列化仍能逐字节回放。
      getAttrs: token => {
        const content = token?.content ?? '';
        return parseMentionSource(content) ?? {
          kind: 'user',
          displayName: null,
          id: null,
          slug: null,
          raw: content
        };
      }
    };
  });
}

/**
 * 腿 C —— 把节点原样还原成提及源码。
 *
 * 这里用 state.write() 而不是 state.text()：
 *   fof/rich-text 重写过 MarkdownSerializerState.prototype.esc（会转义 ` * \ ~ 等字符），
 *   而 state.write() **不做转义**，正是「原样还原提及源码」所需要的 —— 保证落库内容
 *   与改造前逐字节一致，服务端 Flarum\Mentions\ConfigureMentions 的五条正则
 *   （含帖提及 / 用户提及 / 群组提及 / 标签提及）才能继续命中，@提醒与通知链路不受影响
 *   （extend.php 第四节红线；先例：packages/upload 与 fof/rich-text 的 math_block 同样用 state.write）。
 */
function registerSerializer() {
  extend(MARKDOWN_SERIALIZER_BUILDER, 'buildNodes', function (nodes) {
    if (!nodes) return;
    nodes[MENTION_NODE] = (state, node) => {
      state.write(formatMentionSource(node.attrs));
    };
  });
}

/**
 * 补丁 D —— 让「自动完成下拉选中」的提及**当场渲染成小标签**。
 *
 * 【问题现象】
 *   工具栏点 `@` → 输入几个字母 → 在下拉里点选某个用户 / 帖子，
 *   输入框回显的是 `@"admin"#p35` 这样的**源码原文**（而不是「↩ admin」小标签）。
 *
 * 【根因（读 vendor 源码定位，两步链）】
 *   ① vendor/flarum/mentions/js/src/forum/addComposerAutocomplete.js:49
 *        this.attrs.composer.editor.replaceBeforeCursor(autocompleting.absoluteStart - 1, replacement + ' ');
 *      调用时**没传第三个参数 escape**；
 *   ② vendor/fof/rich-text/js/src/common/tiptap/TiptapEditorDriver.ts
 *        replaceBeforeCursor(start, text, escape) { this.insertBetween(start, …, text, escape); }
 *        insertBetween(start, end, text, escape = true) { … }
 *      ⇒ escape 落到默认值 **true** ⇒ 走 `view.state.tr.insertText(text, start, end)`，
 *      纯文本插入、**不经过 markdown 解析器**，所以自研 mention 节点根本没机会被创建。
 *   （对照：帖子「回复」按钮走 reply.js → insertAtCursor(text, false) → escape=false →
 *     `parser.parse()` 解析成 mention 节点，因此那条路径是正常的。）
 *
 * 【修复思路】
 *   不改 vendor/：用 `override()` 包住 `replaceBeforeCursor`，只在「即将插入的内容确实是
 *   提及源码」时改为**直接插入 mention 节点**；其余情况（emoji 自动完成等）原样透传。
 *   为什么不用 `original(start + 1, text, false)` 复用 escape=false 分支：
 *   该分支内部会 `start -= 1` 并按**块级内容**做 replaceRangeWith，在段落中间插入时会把
 *   当前段落**劈成两段**（正文里凭空多出一个换行）。insertContentAt 插入的是**内联**节点，
 *   不会拆分段落，光标与排版都保持原位。
 *
 * 【为什么用 override 而不是 extend】
 *   extend 的回调只能原地 mutate 返回值、无法改变返回值；override 会
 *   `return newMethod.apply(this, [original?.bind(this), …args])`，可以拦下并改写行为。
 *
 * 【安全性】
 *   · 判据是 `parseMentionSource(text.trim())`，只认 @ / # 开头的五种提及形态；
 *     emoji 自动完成传的是 emoji 字符或 `:shortcode:`，解析恒为 null ⇒ 走原逻辑，不受影响。
 *   · 提及节点序列化仍走腿 C 的 `state.write(raw)`，落库字节与改造前完全一致。
 */
function registerAutocompleteInsertion() {
  override(TIPTAP_EDITOR_DRIVER, 'replaceBeforeCursor', function (original, start, text, escape) {
    const attrs = typeof text === 'string' ? parseMentionSource(text.trim()) : null;

    // 非提及内容（emoji 自动完成、将来别处的自动完成）：保持上游默认行为。
    if (!attrs || !this.editor || !this.editor.schema.nodes[MENTION_NODE]) {
      return original?.(start, text, escape);
    }

    // 替换区间 = [@ 所在位置, 当前光标)。start 由调用方传 autocompleting.absoluteStart - 1 得到，
    // 正好是 ProseMirror 文档坐标里 `@` 的位置（AutocompleteReader.absoluteStart 指向 @ 之后一位）。
    const end = this.getSelectionRange()[0];
    if (end <= start) {
      return original?.(start, text, escape);
    }

    // 调用方传进来的是 `replacement + ' '`；把尾部空白保留下来，光标才不会贴死在原子节点上。
    const trailing = text.slice(text.trimEnd().length);
    const content = [{
      type: MENTION_NODE,
      attrs
    }];
    if (trailing) content.push({
      type: 'text',
      text: trailing
    });

    // insertContentAt 默认 updateSelection=true，插入后光标自动落到小标签之后。
    // 派发事务会触发编辑器的 onTransaction / onUpdate ⇒ 界面重绘、内容重新序列化，无需手动 redraw。
    this.editor.chain().insertContentAt({
      from: start,
      to: end
    }, content).run();
  });
}

/** 一次性注册三腿 + 自动完成插入补丁。由 src/forum/index.ts 在 jiedh-mention 初始化器里调用。 */
export function registerMentions() {
  registerNode();
  registerParser();
  registerSerializer();
  registerAutocompleteInsertion();
}